import dayjs from 'dayjs'
import type { BaseQueryErrorShape } from '../../store/api/baseQuery'
import type {
  A2p10dlcConfirmationOverview,
  ConfirmA2p10dlcPayload,
  SigningProviderKindRow,
  SigningProviderOverview,
  SigningProviderValue,
  SigningReadiness,
  SigningReadinessCheck,
  WithdrawA2p10dlcPayload,
} from '../../store/api/adminApi'

/**
 * Pure rules behind the subscriber E-signature tab (approved mockup v2 2026-09-24, v3 2026-10-01), kept out of
 * the component so `unit/eSignatureModel.spec.ts` can pin them without a browser.
 *
 * The SERVER enforces every rule here again (reason required, in-house only for a built kind and
 * only when every readiness check passes, rollback never gated). This file only decides what the
 * operator is shown and when a control is enabled.
 *
 * Transition-phase screen: the per-kind BoldSign / In-house toggle goes away when BoldSign is
 * decommissioned; the readiness checks stay.
 */

export const REASON_MAX_LENGTH = 500

/** `plural` for "New customer contracts ..."; `count` for "3 BoldSign contracts ..." (the mockup's words). */
const KIND_LABELS: Record<string, { label: string; plural: string; count: [string, string] }> = {
  customerContract: { label: 'Customer contract', plural: 'customer contracts', count: ['contract', 'contracts'] },
  changeOrder: { label: 'Change order', plural: 'change orders', count: ['change order', 'change orders'] },
  subAgreement: { label: 'Sub agreement', plural: 'sub agreements', count: ['sub agreement', 'sub agreements'] },
  noticeToOwner: { label: 'Notice to owner', plural: 'notices to owner', count: ['notice', 'notices'] },
  propertyDisclosure: {
    label: 'Property disclosure',
    plural: 'property disclosures',
    count: ['disclosure', 'disclosures'],
  },
}

/**
 * The two job-document kinds. The mockup's sub-label said "lifecycle document"; the owner's
 * standing rule (nhrapp/CLAUDE.md, 2026-08-17) is that the word "lifecycle" never reaches a
 * screen and the family is "Job Document", so this says "job document".
 */
const JOB_DOCUMENT_KINDS = new Set(['noticeToOwner', 'propertyDisclosure'])

export const kindLabel = (kind: string): string => KIND_LABELS[kind]?.label ?? kind

export const kindPlural = (kind: string): string => KIND_LABELS[kind]?.plural ?? kind

/** The small line under the kind name: its key, plus the family for job documents. */
export const kindSubLabel = (kind: string): string => (JOB_DOCUMENT_KINDS.has(kind) ? `${kind} · job document` : kind)

export const providerLabel = (provider: SigningProviderValue): string =>
  provider === 'inhouse' ? 'In-house' : 'BoldSign'

export const NOT_BUILT_HINT = 'In-house not built yet for this kind'
export const NOT_READY_HINT = 'Readiness checks incomplete'

/**
 * Why the In-house option of a row is disabled, or `null` when it may be pressed. Already
 * in-house is not "disabled" - it is the selected option.
 */
export const inhouseDisabledReason = (row: SigningProviderKindRow, readiness: SigningReadiness): string | null => {
  if (row.effective === 'inhouse') return null
  if (!row.implemented) return NOT_BUILT_HINT
  if (!readiness.ready) return NOT_READY_HINT
  return null
}

/**
 * What a click on the row's toggle asks for, or `null` for nothing. MUI's exclusive group reports
 * `null` when the SELECTED option is clicked again. BoldSign is never gated (rollback must always
 * be possible), and that includes a stored in-house the server ignores on a kind not built for it:
 * it shows as BoldSign, so re-clicking BoldSign is how it gets cleared.
 */
export const nextTarget = (
  row: SigningProviderKindRow,
  clicked: SigningProviderValue | null
): SigningProviderValue | null => {
  const deadInhouse = row.stored === 'inhouse' && row.effective === 'boldsign'
  if (clicked === null) return deadInhouse ? 'boldsign' : null
  if (clicked === 'boldsign') return row.effective === 'inhouse' || deadInhouse ? 'boldsign' : null
  return row.effective === 'inhouse' ? null : 'inhouse'
}

export const readinessChip = (readiness: SigningReadiness): { label: string; tone: 'ok' | 'bad' } => ({
  label: `${readiness.passCount} of ${readiness.total} ready`,
  tone: readiness.ready ? 'ok' : 'bad',
})

export const checkStatusLabel = (check: SigningReadinessCheck): string =>
  check.status === 'pass' ? 'Pass' : check.status === 'fail' ? 'Fail' : 'Not checked'

/**
 * The check's detail line, as the server words it. The A2P check also carries `since`, but its
 * detail already reads "Confirmed by <name> on <date>: <note>", so no date is added here.
 */
export const checkDetail = (check: SigningReadinessCheck): string => check.detail

export interface LastChangedView {
  primary: string
  secondary: string | null
}

export const lastChangedView = (row: SigningProviderKindRow): LastChangedView => {
  const change = row.lastChange
  if (!change) return { primary: 'Never changed', secondary: row.implemented ? null : NOT_BUILT_HINT }
  const who = change.byName || change.byId
  return {
    primary: change.at ? dayjs(change.at).format('MMM D, YYYY, h:mm A') : 'Date unknown',
    secondary: `by ${who} (platform)`,
  }
}

export const outForSignatureLine = (kind: string, count: number): string => {
  const [one, many] = KIND_LABELS[kind]?.count ?? [kind, kind]
  if (count === 0) return `No BoldSign ${many} are still out for signature.`
  return count === 1
    ? `1 BoldSign ${one} is still out for signature and will finish at BoldSign.`
    : `${count} BoldSign ${many} are still out for signature and will finish at BoldSign.`
}

export interface BannerView {
  tone: 'info' | 'ok' | 'warn'
  title: string
  lines: string[]
}

/**
 * The banner above the table. With a kind in-house and a check that no longer passes, the toggle
 * STAYS on (nothing is switched off automatically) and the banner says so.
 */
export const bannerView = (overview: SigningProviderOverview): BannerView => {
  const inhouse = overview.kinds.filter((row) => row.effective === 'inhouse')
  if (!inhouse.length) {
    return overview.readiness.ready
      ? {
          tone: 'info',
          title: 'Every document kind is signed with BoldSign.',
          lines: ['The readiness checks pass, so in-house signing can be turned on for a kind below.'],
        }
      : {
          tone: 'info',
          title: 'Every document kind is signed with BoldSign.',
          lines: ['In-house signing cannot be enabled until the readiness checks below pass.'],
        }
  }

  const nouns = inhouse.map((row) => kindPlural(row.kind))
  const title = `New ${nouns.join(', ')} are sent in-house.`
  const counts = inhouse
    .filter((row) => typeof overview.boldsignOutForSignature[row.kind] === 'number')
    .map((row) => outForSignatureLine(row.kind, overview.boldsignOutForSignature[row.kind] ?? 0))

  if (!overview.readiness.ready) {
    return {
      tone: 'warn',
      title: 'A readiness check no longer passes.',
      lines: [
        `New ${nouns.join(', ')} are still sent in-house; nothing was switched off. Review the checks below.`,
        ...counts,
      ],
    }
  }
  return { tone: 'ok', title, lines: counts }
}

export interface DialogCopy {
  title: string
  /** In-house only: the company named in "For <company>, from now on:". */
  introCompany: string | null
  bullets: { strong?: string; text: string }[]
  confirmLabel: string
  /** In-house only: the operator attests the checks were confirmed with the company. */
  requiresAttestation: boolean
}

export const dialogCopy = (kind: string, target: SigningProviderValue, companyName: string): DialogCopy => {
  const plural = kindPlural(kind)
  if (target === 'inhouse') {
    return {
      title: `Send new ${plural} in-house?`,
      introCompany: companyName || 'this company',
      bullets: [
        { text: `New ${plural} are sent and signed in-house.` },
        {
          strong: 'Only new sends change.',
          text: 'Documents already out for signature finish at BoldSign, including their reminders and completion.',
        },
        { text: 'Every other document kind stays on BoldSign.' },
        { strong: 'Rollback is one value.', text: 'Set it back to BoldSign here; nothing needs migrating.' },
      ],
      confirmLabel: 'Switch to in-house',
      requiresAttestation: true,
    }
  }
  return {
    title: `Go back to BoldSign for ${plural}?`,
    introCompany: null,
    bullets: [
      { text: `New ${plural} are sent through BoldSign again.` },
      {
        strong: 'Only new sends change.',
        text: 'In-house envelopes already out for signature keep working and finish in-house, with their record intact.',
      },
      { text: 'Nothing is deleted. You can switch again at any time.' },
    ],
    confirmLabel: 'Switch to BoldSign',
    requiresAttestation: false,
  }
}

/** The confirm button's gate. The server re-checks the reason; this only avoids a pointless call. */
export const canConfirm = (reason: string, target: SigningProviderValue, attested: boolean): boolean => {
  const trimmed = reason.trim()
  if (!trimmed || trimmed.length > REASON_MAX_LENGTH) return false
  return target !== 'inhouse' || attested
}

// -- A2P 10DLC confirmation (approved mockup v3, owner ruling 2026-10-01) --
//
// The server cannot verify a Twilio A2P 10DLC brand + campaign registration, so its readiness check
// passes on a platform admin's RECORDED confirmation (who, when, a note) and is "Not checked" until
// then. Recorded and withdrawn only on this tab, by the same SuperAdmin who may switch the provider;
// the server re-checks the role, the note's length and the reason.

/** The note's bounds after trimming - the server's `A2P10DLC_NOTE_MIN_LENGTH` / `_MAX_LENGTH`. */
export const A2P_NOTE_MIN_LENGTH = 10
export const A2P_NOTE_MAX_LENGTH = 500

export type A2pAction = 'record' | 'withdraw'

/**
 * The action offered on a readiness row, or `null` for none: only on the A2P row, only for an
 * operator who may switch the provider (`canManage`), "Record" while it is not confirmed and
 * "Withdraw" while a confirmation stands.
 */
export const a2pAction = (
  check: SigningReadinessCheck,
  a2p: A2p10dlcConfirmationOverview,
  canManage: boolean
): A2pAction | null => {
  if (!canManage || check.key !== 'a2p10dlc') return null
  if (check.status === 'pass') return a2p.canWithdraw ? 'withdraw' : null
  if (check.status === 'notChecked') return a2p.canWithdraw ? null : 'record'
  return null
}

/** RHF `validate` for the note: what the server will accept, on the trimmed value. */
export const validateA2pNote = (value: string): true | string => {
  const length = (value ?? '').trim().length
  if (!length) return 'Say how you confirmed it'
  if (length < A2P_NOTE_MIN_LENGTH) return `Write at least ${A2P_NOTE_MIN_LENGTH} characters`
  if (length > A2P_NOTE_MAX_LENGTH) return `Keep the note to ${A2P_NOTE_MAX_LENGTH} characters or fewer`
  return true
}

/** The Record button's gate: a valid note AND the attestation ticked. */
export const canRecordA2p = (note: string, attested: boolean): boolean => validateA2pNote(note) === true && attested

/** RHF `validate` for the withdrawal reason: required, at most 500 characters, trimmed. */
export const validateWithdrawReason = (value: string): true | string => {
  const length = (value ?? '').trim().length
  if (!length) return 'A reason is required'
  if (length > REASON_MAX_LENGTH) return `Keep the reason to ${REASON_MAX_LENGTH} characters or fewer`
  return true
}

export const canWithdrawA2p = (reason: string): boolean => validateWithdrawReason(reason) === true

export const a2pConfirmBody = (companyId: string, note: string): ConfirmA2p10dlcPayload => ({
  companyId,
  note: note.trim(),
})

export const a2pWithdrawBody = (companyId: string, reason: string): WithdrawA2p10dlcPayload => ({
  companyId,
  reason: reason.trim(),
})

export const A2P_RECORD_COPY = {
  title: 'Record text messaging registration?',
  noteLabel: 'How you confirmed it (required, saved to the audit log)',
  notePlaceholder: "e.g. Twilio console shows brand and campaign approved for the company's number.",
  attestation: 'I confirmed the brand and campaign are registered for the number this company sends from.',
  confirmLabel: 'Record confirmation',
  success: 'Text messaging registration recorded',
  fallbackError: 'The confirmation could not be recorded. Please try again.',
}

/**
 * Not in the mockup, which shows only the Withdraw link; the words follow the server's own
 * description of the DELETE route: the check goes back to "Not checked" and nothing is switched
 * back, only a NEW in-house switch is held shut.
 */
export const A2P_WITHDRAW_COPY = {
  title: 'Withdraw the text messaging confirmation?',
  bullets: [
    'The text messaging check goes back to Not checked.',
    'Nothing is switched back: kinds already sent in-house stay in-house.',
    'In-house signing cannot be turned on for another kind until it is confirmed again.',
  ],
  reasonLabel: 'Reason (required, saved to the audit log)',
  reasonPlaceholder: 'Why is the confirmation being withdrawn?',
  confirmLabel: 'Withdraw confirmation',
  success: 'Text messaging confirmation withdrawn',
  fallbackError: 'The confirmation could not be withdrawn. Please try again.',
}

/** What a dialog shows for a refused call: the server's own words (a 409 says why), else `fallback`. */
export const dialogFailureMessage = (error: unknown, fallback: string): string => {
  const message = (error as BaseQueryErrorShape | null | undefined)?.message
  return typeof message === 'string' && message.trim() ? message : fallback
}

/**
 * Runs a dialog's mutation (`.unwrap()`ed) and returns `null` on success - the dialog closes - or
 * the message to show inside the still-open dialog.
 */
export const runDialogSubmit = async (call: () => Promise<unknown>, fallback: string): Promise<string | null> => {
  try {
    await call()
    return null
  } catch (error) {
    return dialogFailureMessage(error, fallback)
  }
}
